// Relic decay: one live, reversible Entropy ladder for the whole
// Reliquary/Standard/Weight family -- Pristine below `witheredEntropy`,
// Withered between the two thresholds, Fractured at or above
// `fracturedEntropy`. Structurally rust.ts's mechanism with three stages
// instead of two: a pure function of the object's current
// `properties.entropy`, recomputed every settle, nothing cached or persisted.
//
// **What this replaced** (2026-08-09). The family was authored five different
// ways across three rooms, from three textures, with two mechanisms and one
// object having none:
//
// - room6/room16's Withered Standard and Pristine Reliquary were a
//   `barrier.transformsInto` toggle pair -- one watching `below 4`, the other
//   `above 4`, each swapping to the other's texture and flipping its own rule's
//   direction so the next crossing swapped it back.
// - room8/room16's Fractured Reliquary had no `barrier` at all: a plain Entropy
//   reservoir to mark as a Transfer source.
// - room16's Fractured Weight and Sealed Weight had none either: the
//   `Bind`·`Entropy` pair, borrowing the relic textures.
//
// Three things were wrong with that, and they are one thing underneath. First,
// `resolveBarrier`'s `transformsInto` branch swapped **only the texture** --
// `name`, `interact.text` and `signature` all stayed at whatever was authored,
// so a restored Withered Standard showed a pristine sprite while still reading
// "corroded past any hope of repair" and still *Measuring* 0.85 on Entropy. The
// Measure·Radial overlay contradicted the sprite. Second, the three objects
// with no threshold never changed at all however much Entropy moved through
// them -- worst in room16's bay L, whose entire subject is watching Entropy
// move between the two Weights. Third, a toggled Standard *was* a Reliquary
// mechanically while keeping the other one's name.
//
// The unification: an object's stage is not a thing you author, it is a thing
// you read off the quantity that decides it. `variant` supplies the noun
// (which is the only real difference between a Reliquary, a Standard and a
// Weight -- the same role `RustDef.kind` and `SandDef.variant` play), the stage
// supplies everything else. All five authored objects become the same object
// with a different starting Entropy: 0, 6 and 8.
//
// Why no memory, when sand.ts needs some: sand's ladder is not recoverable from
// its properties (drained-to-Quartz and never-heated both read 0/0, see that
// module's header). A relic has exactly one input and it is monotone in it, so
// live is not just sufficient but strictly better -- it makes decay reversible
// for free, and survives a room revisit without the persisted flag
// `barrier.transformed` needed.
import { ObjectDef, RelicStageId, RelicVariant } from './types';
import { PhysicsConfig, DEFAULT_PHYSICS } from './physics';
import { SimEvents } from './events';
import { WorldState } from './state';
import { applyStagePresentation } from './presentation';

/** The look of each stage, shared by every variant -- what a relic at a given
 * Entropy *is* doesn't depend on what it was built as, only what it is called.
 * Three hand-drawn textures, one per stage, all three of which already existed
 * for the authored objects this ladder replaced. */
export const RELIC_STAGE_TEXTURE: Record<RelicStageId, string> = {
  pristine: 'object-relic-pristine',
  withered: 'object-relic-ruined',
  fractured: 'object-relic-fractured',
};

/** How strongly each stage reads when Measured (`ObjectDef.signature`). Derived
 * rather than authored, which is the whole point: the reading a Measure·Radial
 * cast shows is now the object's actual condition, not a note about the
 * condition it shipped in. The values are the ones the authored objects
 * carried, kept so nothing about the overlay's appearance changes for an
 * un-cast-at world. */
export const RELIC_STAGE_SIGNATURE: Record<RelicStageId, number> = {
  pristine: 0.15,
  withered: 0.85,
  fractured: 0.9,
};

export interface RelicStageMeta {
  name: string;
  description: string;
}

/** Name and flavour per variant × stage. The variant is the noun and the stage
 * is the adjective, so every name the world shipped with survives as a point on
 * this grid rather than as an object of its own -- with one deliberate
 * exception noted on the Weight ladder below. */
export const RELIC_LADDER: Record<RelicVariant, Record<RelicStageId, RelicStageMeta>> = {
  reliquary: {
    pristine: {
      name: 'Pristine Reliquary',
      description: "A reliquary in flawless condition, orderly to a fault -- entropy hasn't touched this one yet. It feels like it's owed some, sooner or later.",
    },
    withered: {
      name: 'Withered Reliquary',
      description: 'A reliquary gone dull and pitted, its seams beginning to part. Something has been working at it, and has not finished.',
    },
    fractured: {
      name: 'Fractured Reliquary',
      description: 'A reliquary already coming apart at the seams. There is a great deal of disorder in it, and nothing holding it in.',
    },
  },
  standard: {
    pristine: {
      name: 'Pristine Standard',
      description: 'A standard restored to its full height, the cloth whole and the shaft true. Whatever was eating at it has been taken elsewhere.',
    },
    withered: {
      name: 'Withered Standard',
      description: "A standard, corroded past any hope of repair on its own -- entropy has had its way with this one. Whatever wears an object down this badly has to come from somewhere; it doesn't just vanish.",
    },
    fractured: {
      name: 'Fractured Standard',
      description: 'A standard broken off at the base, its cloth gone to threads. There is far more disorder in it than it can hold.',
    },
  },
  // The Weight ladder renames one shipped object rather than the other way
  // round: room16's drive weight was authored "Fractured Weight" while holding
  // Entropy 6, which is this ladder's *middle* stage, not its last. Straining
  // is what 6 actually looks like, and it leaves "Fractured Weight" meaning the
  // same thing here that "Fractured Reliquary" does -- which is the point of
  // having one ladder.
  weight: {
    pristine: {
      name: 'Sealed Weight',
      description: 'A counterweight, sealed and orderly, sitting exactly as heavy and exactly as still.',
    },
    withered: {
      name: 'Straining Weight',
      description: 'A counterweight straining under whatever it has been holding, its casing beginning to craze.',
    },
    fractured: {
      name: 'Fractured Weight',
      description: 'A counterweight fractured through under its load. There is a great deal of disorder in it.',
    },
  },
};

/** Which stage an object's current Entropy puts it in. Pure, and cheap enough
 * to call on every settle rather than caching -- the object's own
 * `properties.entropy` is the only state that matters, exactly as `rustStage`
 * has it. */
export function relicStage(obj: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS): RelicStageId {
  const entropy = obj.properties?.entropy ?? 0;
  if (entropy >= physics.relic.fracturedEntropy) return 'fractured';
  return entropy >= physics.relic.witheredEntropy ? 'withered' : 'pristine';
}

/** Re-derives a relic's whole presentation -- name, texture, description and
 * Measured signature -- from the stage it currently reads.
 *
 * Writes `obj.texture` back directly rather than only comparing against it,
 * for the reason `refreshRustTexture` gives at length: this ladder is
 * reversible, so a stage that cycles back to its original authored texture
 * would compare equal to a stale field and skip re-emitting, leaving a sprite
 * stuck on the previous look. `name` and `signature` are mutated in place the
 * same way -- nothing else in `sim/` writes either field, and both are read
 * straight off the object (the faced-target label, Measure·Radial) rather than
 * through an event. */
export function refreshRelicState(obj: ObjectDef, physics: PhysicsConfig, out: SimEvents): void {
  if (!obj.relic) return;
  const stage = relicStage(obj, physics);
  const meta = RELIC_LADDER[obj.relic.variant ?? 'reliquary'][stage];

  // Name, texture and description move together -- they are three parts of one
  // answer to "what am I looking at", and the whole bug this ladder exists to
  // fix was letting them drift apart. Shared with every other ladder since
  // 2026-08-11 (`presentation.ts`), which is where that reasoning now lives:
  // this was the only ladder that got it right, so it became the one the others
  // were made to match.
  applyStagePresentation(obj, { name: meta.name, texture: RELIC_STAGE_TEXTURE[stage], description: meta.description }, out, 'cross-fade');

  const signature = RELIC_STAGE_SIGNATURE[stage];
  if (obj.signature?.entropy !== signature) obj.signature = { ...obj.signature, entropy: signature };
}

/** The declared system's own body (see settle.ts): re-derives every relic's
 * presentation for whatever just changed. */
export function settleRelics(world: WorldState, out: SimEvents): void {
  const room = world.rooms[world.player.roomId];
  if (!room) return;
  for (const obj of room.objects) {
    if (obj.relic) refreshRelicState(obj, world.physics, out);
  }
}
